import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera } from 'lucide-react';
import { Reveal } from '../../hooks/useReveal';

/**
 * "Our Work, Captured" — premium photo strip featuring 4 hyper-realistic
 * pest control treatment images. Each card lifts + zooms on hover and
 * fades in on scroll.
 */
const TREATMENTS = [
  {
    slug: 'fogging',
    title: 'Mosquito & Outdoor Fogging',
    subtitle: 'Cinematic mist treatment',
    img: '/images/pest-control/fogging.png',
    href: '/pricing/mosquito-fogging',
    accent: 'from-rose-600/80 to-red-700/80',
  },
  {
    slug: 'rodent-control',
    title: 'Rodent Control',
    subtitle: 'Tamper-proof bait stations',
    img: '/images/pest-control/rodent-control.png',
    href: '/pricing/rodent-control',
    accent: 'from-stone-700/80 to-zinc-900/80',
  },
  {
    slug: 'cockroach-treatment',
    title: 'Cockroach Treatment',
    subtitle: 'Precision gel baiting',
    img: '/images/pest-control/cockroach-treatment.png',
    href: '/pricing/cockroach-control',
    accent: 'from-emerald-700/80 to-teal-800/80',
  },
  {
    slug: 'bed-bug-treatment',
    title: 'Bed Bug Treatment',
    subtitle: 'Steam-wand + chemical combo',
    img: '/images/pest-control/bed-bug-treatment.png',
    href: '/pricing/bed-bug-removal',
    accent: 'from-amber-700/80 to-rose-800/80',
  },
  {
    slug: 'anti-termite-treatment',
    title: 'Anti-Termite Treatment',
    subtitle: 'Chemical barrier injection',
    img: '/images/pest-control/anti-termite-treatment.png',
    href: '/pricing/anti-termite-treatment',
    accent: 'from-orange-700/80 to-amber-900/80',
  },
];

export default function TreatmentShowcase() {
  return (
    <section
      className="relative py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden"
      data-testid="treatment-showcase"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <Reveal>
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 mb-4">
              <Camera size={13} /> Our Work, Captured
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3"
              style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            >
              See the <span className="italic text-brand-green">Treatment</span> in Action
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Real moments from our certified technicians at work — fogging, rodent control,
              cockroach baiting, bed-bug elimination and anti-termite treatment, all done with
              premium equipment and family-safe chemicals.
            </p>
          </div>
        </Reveal>

        {/* 5-card image grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {TREATMENTS.map((t, i) => (
            <Reveal key={t.slug} delay={i * 0.08}>
              <Link
                to={t.href}
                data-testid={`treatment-card-${t.slug}`}
                className="group relative block rounded-2xl overflow-hidden tipc-card-hover h-[360px] sm:h-[380px] lg:h-[420px] bg-slate-900"
              >
                {/* Image */}
                <img
                  src={t.img}
                  alt={t.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                  onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
                />
                {/* Gradient overlay — always present, deepens on hover */}
                <div className={`absolute inset-0 bg-gradient-to-t ${t.accent} mix-blend-multiply opacity-50 group-hover:opacity-65 transition-opacity duration-500`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

                {/* Title block */}
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-white">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80 mb-1.5 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    {t.subtitle}
                  </div>
                  <h3
                    className="text-xl sm:text-2xl font-bold leading-tight mb-2 transition-transform duration-500 group-hover:-translate-y-0.5"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {t.title}
                  </h3>
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-300">
                    See pricing
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>

                {/* Top-right "AI photo" badge for the curious */}
                <div className="absolute top-3 right-3 bg-white/10 backdrop-blur-sm border border-white/15 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  Captured by EntoRid
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
